<script setup lang="ts">
type TItem = {
  title: string
  content: string | TNavigationLinkModel | TNavigationLinkModel[]
}

export type TProps = TBuilderBaseProps & {
  items: TItem[]
  allowExpandMultiple?: boolean
  initialOpenedItems?: TNullable<number[]> | TNullable<string>
}

const DEFAULT_OPEN_ITEM = 0

const props = withDefaults(defineProps<TProps>(), {
  allowExpandMultiple: false,
  initialOpenedItems: () => [DEFAULT_OPEN_ITEM],
  attributes: () => ({}),
})

const expandedPanels = ref<number[]>([])

watch(
  () => props.initialOpenedItems,
  async (initialPanels) => {
    if (!initialPanels) {
      return
    }

    await nextTick()

    let filteredPanels: number[] = [DEFAULT_OPEN_ITEM]

    if (Array.isArray(initialPanels)) {
      filteredPanels = initialPanels
    }

    if (typeof initialPanels === 'string') {
      filteredPanels = initialPanels
        .split(',')
        .map((i) => i.replaceAll(/\D/g, ''))
        .filter(Boolean)
        .map(Number)
    }

    if (!props.allowExpandMultiple) {
      const firstPanel = filteredPanels[0]
      if (firstPanel !== undefined) {
        expandedPanels.value = [firstPanel]
      }
      return
    }

    expandedPanels.value = filteredPanels.splice(0, props.items.length)
  },
  { immediate: true }
)

const resetExpandedPanels = () => {
  expandedPanels.value = []
}

watch(
  () => props.allowExpandMultiple,
  () => resetExpandedPanels(),
  { immediate: true }
)

const togglePanel = (index: number) => {
  if (
    !props.allowExpandMultiple &&
    (!expandedPanels.value || !Array.isArray(expandedPanels.value))
  ) {
    expandedPanels.value = [index]
    return
  }

  if (!props.allowExpandMultiple && Array.isArray(expandedPanels.value)) {
    expandedPanels.value = expandedPanels.value[0] === index ? [] : [index]
    return
  }

  if (!expandedPanels.value) {
    expandedPanels.value = [index]
    return
  }

  expandedPanels.value = expandedPanels.value.includes(index)
    ? expandedPanels.value.filter((i) => i !== index)
    : [...expandedPanels.value, index]
}
</script>

<template>
  <div
    v-if="props.items?.length"
    class="inline-flex flex-col justify-center items-start w-full max-w-full"
  >
    <BaseAccordionPanel
      v-for="(item, index) in props.items"
      :key="`accordion-panel-${index}`"
      :title-aria-label="item.title"
      :title="item.title"
      :show="expandedPanels.includes(index)"
      @toggle:show="togglePanel(index)"
    >
      <template #title>
        <slot
          name="title"
          :is-open="expandedPanels.includes(index)"
          :item="item"
          :index="index"
        >
          <h3>{{ item.title }}</h3>
        </slot>
      </template>

      <slot
        :is-open="expandedPanels.includes(index)"
        :item="item"
        :index="index"
      >
        <h4
          class="pb-6 will-change-transform text-trail-body2 text-trail-text-body-normal"
        >
          {{ item.content }}
        </h4>
      </slot>
    </BaseAccordionPanel>
  </div>
</template>
